import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectStatus, mayRequestPullTestingLoadTestLogReport, mayUsePullTestingDashboardActions } from 'shared_models';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';
import { ProjectFacadeService } from '../services/project-facade.service';
import { PullTestingService } from '../services/pull-testing.service';
import { getProjectIdFromRoute } from './route-param.util';

async function pullTestingModuleAllowsOperatorWrite(
  projectID: string,
  projectFacade: ProjectFacadeService
): Promise<boolean> {
  await projectFacade.loadProjectModules(projectID);
  const snap = projectFacade.getSnapshot();
  if (!snap.project || snap.project.status !== ProjectStatus.Active) {
    return false;
  }
  if (snap.pullTestingModule?.status === 'completed') {
    return false;
  }
  return true;
}

/**
 * Restricts the Pull Testing report route to privileged roles (see {@link mayRequestPullTestingLoadTestLogReport}).
 */
export const pullTestingReportRoleGuard: CanActivateFn = async route => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const projectID = getProjectIdFromRoute(route);

  let user;
  try {
    user = await firstValueFrom(authService.getCurrentUser());
  } catch {
    return router.parseUrl('/login');
  }
  if (!user) {
    return router.parseUrl('/login');
  }

  if (mayRequestPullTestingLoadTestLogReport(user.role)) {
    return true;
  }

  logger.warn('[PullTestingReportRoleGuard] Report generation not allowed for role:', user.role);
  if (projectID) {
    return router.parseUrl(`/project/${projectID}/pull-testing`);
  }
  return router.parseUrl('/landing');
};

/**
 * Setup wizard: privileged roles may always open (create load set / append pilings).
 * Other roles may open only for first-time module setup when pull testing is not configured yet (404).
 */
export const pullTestingSetupRoleGuard: CanActivateFn = async route => {
  const authService = inject(AuthService);
  const pullTestingService = inject(PullTestingService);
  const projectFacade = inject(ProjectFacadeService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const projectID = getProjectIdFromRoute(route);

  let user;
  try {
    user = await firstValueFrom(authService.getCurrentUser());
  } catch {
    return router.parseUrl('/login');
  }
  if (!user) {
    return router.parseUrl('/login');
  }

  if (!projectID) {
    return router.parseUrl('/landing');
  }

  if (mayRequestPullTestingLoadTestLogReport(user.role)) {
    const allowsWrite = await pullTestingModuleAllowsOperatorWrite(projectID, projectFacade);
    if (!allowsWrite) {
      logger.warn('[PullTestingSetupRoleGuard] Module is read-only (inactive project or completed module).');
      return router.parseUrl(`/project/${projectID}/pull-testing`);
    }
    return true;
  }

  try {
    const res = await firstValueFrom(pullTestingService.getModuleConfig(projectID));
    if (res?.data) {
      logger.warn('[PullTestingSetupRoleGuard] Load set creation not allowed for role:', user.role);
      return router.parseUrl(`/project/${projectID}/pull-testing`);
    }
  } catch (e) {
    const err = e as HttpErrorResponse;
    if (err?.status === 404) {
      return true;
    }
  }

  logger.warn('[PullTestingSetupRoleGuard] Setup wizard not allowed for role:', user.role);
  return router.parseUrl(`/project/${projectID}/pull-testing`);
};

/**
 * Restricts install / test operator routes to roles that may act on pull testing (not {@link UserRole.Viewer}).
 */
export const pullTestingOperatorRoleGuard: CanActivateFn = async route => {
  const authService = inject(AuthService);
  const projectFacade = inject(ProjectFacadeService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const projectID = getProjectIdFromRoute(route);

  let user;
  try {
    user = await firstValueFrom(authService.getCurrentUser());
  } catch {
    return router.parseUrl('/login');
  }
  if (!user) {
    return router.parseUrl('/login');
  }

  if (!mayUsePullTestingDashboardActions(user.role)) {
    logger.warn('[PullTestingOperatorRoleGuard] Install/test not allowed for role:', user.role);
    if (projectID) {
      return router.parseUrl(`/project/${projectID}/pull-testing`);
    }
    return router.parseUrl('/landing');
  }

  if (!projectID) {
    return router.parseUrl('/landing');
  }

  const allowsWrite = await pullTestingModuleAllowsOperatorWrite(projectID, projectFacade);
  if (!allowsWrite) {
    logger.warn('[PullTestingOperatorRoleGuard] Module is read-only (inactive project or completed module).');
    return router.parseUrl(`/project/${projectID}/pull-testing`);
  }

  return true;
};
